import { Button, Card, CardContent, Tab, Tabs, Typography } from "@mui/material";
import Chip from "@mui/material/Chip";
import InputLabel from "@mui/material/InputLabel";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useTheme } from "@mui/material/styles";
import * as React from "react";
import { useBasket } from "../basket/basketContext";
import { BasketItem } from "../basket/basketData";
import { LocaleSelectMaterial } from "../components/countySelect";

import { Add, Edit } from "@mui/icons-material";
import { Box, CardMedia, IconButton } from "@mui/material";
import { Title } from "react-admin";
import Currency from "../components/currency";
import "../css/cart.css";

import { styled } from "@mui/material/styles";
import { useStateMachine } from "little-state-machine";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { __DEV__, processENV } from "../api";
import CartList from "../components/cartList";
import getTranslationJson from "../components/getTranslationJson";
import RedditTextField from "../components/redditTextfield";
import { clearStateAction, updateDataAction } from "../globalState";
import { useBundles } from "../hooks/useBundles";
import { useCart } from "../hooks/useCheckout";
import { useCoupon } from "../hooks/useCoupon";
import { useLocales } from "../hooks/useLocales";
import { useProducts } from "../hooks/useProducts";
import dataProvider from "../providers/grapheneDataProvider2";

const sort = { field: "name", order: "DESC" };
const LazyLoadedBasketOrdersTab = React.lazy(() => import("../components/BasketOrdersTab"));

type BasketPromotionCode = GQL.PromotionCode & {
  endDate?: string | null;
  orderCount?: number | null;
  productIds?: string[] | string | null;
};

function parseDelimitedValues(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(Boolean) as string[];
  }

  if (typeof value === "string" && value.trim() !== "") {
    return value.split(",").map((item) => item.trim());
  }

  return [];
}

const getPromotionCodeKey = (
  promotionCode: Partial<BasketPromotionCode> | null | undefined,
  fallbackIndex?: number
): string => {
  const normalizedProductIds = parseDelimitedValues(promotionCode?.productIds).sort().join(",");

  return (
    [
      promotionCode?.id || "",
      promotionCode?.stripePromotionCodeId || "",
      promotionCode?.couponCode || "",
      promotionCode?.country || "",
      normalizedProductIds,
    ]
      .filter(Boolean)
      .join("|") || `promotion-code-${fallbackIndex ?? 0}`
  );
};

const dedupePromotionCodes = (codes: Array<BasketPromotionCode | null | undefined>): BasketPromotionCode[] => {
  const seen = new Set<string>();

  return codes.reduce<BasketPromotionCode[]>((uniqueCodes, promotionCode, index) => {
    if (!promotionCode) {
      return uniqueCodes;
    }

    const promotionCodeKey = getPromotionCodeKey(promotionCode, index);
    if (seen.has(promotionCodeKey)) {
      return uniqueCodes;
    }

    seen.add(promotionCodeKey);
    uniqueCodes.push(promotionCode);
    return uniqueCodes;
  }, []);
};

export const basketItemsToCartItems = (basketItems: BasketItem[]): GQL.LineItemInput[] => {
  let items: any = basketItems;
  items.map((item: any) => {
    for (var k in item) {
      if (k === "price_id") {
        item["price"] = item[k];
      }
      if (k !== "price" && k !== "quantity") {
        delete item[k];
      }
    }
    return item;
  });
  return items;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Box>{children}</Box>
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}
export default function Basket() {
  const theme = useTheme();
  const { actions, state } = useStateMachine({ updateDataAction, clearStateAction });
  const [isInitiallyFetched, setIsInitiallyFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cart, setCart] = useState<GQL.Cart>({});
  const [selectedCountry, setSelectedCountry] = useState<string>("GB");
  const [couponInput, setCouponInput] = useState("");
  const basket = useBasket();
  const mutationCart = useCart();
  const mutationCouponCheck = useCoupon();
  let navigate = useNavigate();
  const [translation, setTranslation] = useState<GQL_AEM.CheckoutTranslationModels | null>(null);

  const queryLocales = useLocales();
  const [tabValue, setTabValue] = React.useState(0);
  const [tabValue2, setTabValue2] = React.useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  const handleTabChange2 = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue2(newValue);
  };

  const [currencyCart, setCurrencyCart] = React.useState("GBP");
  const [languageCart, setLanguageCart] = React.useState("EN");
  const [emailTypeCart, setEmailTypeCart] = React.useState("");
  const [locale, setLocale] = React.useState<string>("");
  const [promotionCodes, setPromotionCodes] = useState<BasketPromotionCode[]>([]);
  const [promotionCodesLoading, setPromotionCodesLoading] = useState(false);
  const [promotionCodesError, setPromotionCodesError] = useState<string | null>(null);

  const [productsParams, setProductsParams] = useState({
    country: selectedCountry,
    currency: currencyCart,
    language: languageCart,
  });

  const [bundlesParams, setBundlesParams] = useState({
    country: selectedCountry,
    currency: currencyCart,
    language: languageCart,
  });

  const products = useProducts();
  const bundles = useBundles();

  const handleEmailTypeCartChange = (event: React.MouseEvent<HTMLElement>, newValue: string) => {
    if (newValue !== null) {
      setEmailTypeCart(newValue);
    }
  };

  const handleLocaleChange = (newLocale: string) => {
    if (newLocale !== null && newLocale !== undefined) {
      let locale = JSON.parse(newLocale) as GQL.LocaleModel;
      setLanguageCart(locale.language.toUpperCase());
      setSelectedCountry(locale.country);
      setCurrencyCart(locale.currency);

      setLocale(newLocale);
    }
  };

  useEffect(() => {
    if (isInitiallyFetched) {
      if (__DEV__) {
        console.log("STATE", state);
      }
      if (languageCart) {
        setTranslation(getTranslationJson(languageCart));
      }
      let cartItemListSimple =
        cart && cart.lineItems
          ? cart.lineItems.map((lineItem) => {
              return { lineId: lineItem?.lineId, quantity: lineItem?.quantity };
            })
          : [];
      let cartData: GQL.MutationCartArgs = {
        currency: currencyCart,
        lineItems: cart.lineItems ? cartItemListSimple : [],
        cartId: state.data.cartId,
        country: selectedCountry,
        language: languageCart,
        emailType: emailTypeCart,
      };
      mutationCart.mutate(cartData, {
        onSuccess: (data) => {
          actions.updateDataAction({ cartId: data.cartId });
          setCart(data);
        },
      });

      setIsLoading(false);
    } else {
      actions.clearStateAction({});
      setIsInitiallyFetched(true);
    }
  }, [isInitiallyFetched, selectedCountry, languageCart, currencyCart]);

  useEffect(() => {
    let isActive = true;
    const bundleIdsInCart = new Set((cart.lineItems || []).map((lineItem) => lineItem?.lineId).filter(Boolean));
    const productIdsInCart = Array.from(
      new Set(
        (bundles.data || [])
          .filter((bundle: any) => bundleIdsInCart.has(bundle.bundleId))
          .flatMap((bundle: any) =>
            (bundle.productsExtended || []).map((product: any) => product?.productId).filter(Boolean)
          )
      )
    );

    if (productIdsInCart.length === 0) {
      setPromotionCodes([]);
      setPromotionCodesLoading(false);
      setPromotionCodesError(null);
      return;
    }

    setPromotionCodesLoading(true);
    setPromotionCodesError(null);

    dataProvider
      .getList("promotionCode", {
        sort: { field: "createdAt", order: "DESC" },
        pagination: { page: 1, perPage: 500 },
        filter: { active: true, products__in: productIdsInCart },
      })
      .then(({ data }) => {
        if (!isActive) return;
        setPromotionCodes(dedupePromotionCodes((data || []) as Array<BasketPromotionCode | null | undefined>));
      })
      .catch((error) => {
        if (!isActive) return;
        setPromotionCodesError(error?.message || "Failed to load promotion codes");
      })
      .finally(() => {
        if (!isActive) return;
        setPromotionCodesLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [bundles.data, cart.lineItems]);

  const productPromotionCodeGroupsInCart = useMemo(() => {
    const bundleIdsInCart = new Set((cart.lineItems || []).map((lineItem) => lineItem?.lineId).filter(Boolean));
    const bundleProductsInCart = new Map<string, string>();

    (bundles.data || []).forEach((bundle: any) => {
      if (!bundleIdsInCart.has(bundle.bundleId)) return;
      (bundle.productsExtended || []).forEach((product: any) => {
        if (product?.productId) {
          bundleProductsInCart.set(product.productId, product.name || product.productId);
        }
      });
    });

    return Array.from(bundleProductsInCart.entries())
      .map(([productId, productName]) => {
        const matchingPromotionCodes = promotionCodes.filter((promotionCode) => {
          const productIds = parseDelimitedValues(promotionCode.productIds);
          const countries = parseDelimitedValues(promotionCode.country);
          const isDeactivated = promotionCode.active === false;
          const isExpired = Boolean(promotionCode.endDate && new Date(promotionCode.endDate) < new Date());
          return productIds.includes(productId) && countries.includes(selectedCountry) && !isDeactivated && !isExpired;
        });
        return { productId, productName, promotionCodes: matchingPromotionCodes };
      })
      .filter((item) => item.promotionCodes.length > 0);
  }, [bundles.data, cart.lineItems, promotionCodes, selectedCountry]);

  const appliedCouponEligibleProductIds = useMemo(() => {
    if (!cart.couponCode) {
      return [];
    }

    const normalizedCouponCode = cart.couponCode.trim().toUpperCase();
    const appliedPromotionCode = promotionCodes.find((promotionCode) => {
      const countries = parseDelimitedValues(promotionCode.country);
      const isDeactivated = promotionCode.active === false;
      const isExpired = Boolean(promotionCode.endDate && new Date(promotionCode.endDate) < new Date());

      return (
        promotionCode.couponCode?.trim().toUpperCase() === normalizedCouponCode &&
        countries.includes(selectedCountry) &&
        !isDeactivated &&
        !isExpired
      );
    });

    return parseDelimitedValues(appliedPromotionCode?.productIds);
  }, [cart.couponCode, promotionCodes, selectedCountry]);

  const onCouponInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setCouponInput(newValue);
  };

  const setCouponBtn = (couponCodeOverride?: string) => {
    const couponCodeToApply = couponCodeOverride ?? couponInput;
    if (couponCodeToApply === "") {
      let cartData: GQL.MutationCartArgs = {
        cartId: state.data.cartId,
        couponCode: couponCodeToApply,
        country: selectedCountry,
        language: languageCart,
        currency: currencyCart,
        emailType: emailTypeCart,
      };
      mutationCart.mutate(cartData, {
        onSuccess: (data) => {
          actions.updateDataAction({ cartId: data.cartId });
          setCart(data);
        },
      });
    } else {
      mutationCouponCheck.mutate(
        { couponCode: couponCodeToApply, country: selectedCountry },
        {
          onSuccess: (data) => {
            if (data.valid) {
              let cartData: GQL.MutationCartArgs = {
                cartId: state.data.cartId,
                couponCode: couponCodeToApply,
                language: languageCart,
                country: selectedCountry,
                currency: currencyCart,
                emailType: emailTypeCart,
              };
              mutationCart.mutate(cartData, {
                onSuccess: (data) => {
                  actions.updateDataAction({ cartId: data.cartId });
                  setCart(data);
                },
              });
            } else {
            }
          },
        }
      );
    }
  };

  const handleCartUpdatedFromOrder = (updatedCart: GQL.Cart) => {
    actions.updateDataAction({ cartId: updatedCart.cartId });
    setCart(updatedCart);
    setCouponInput(updatedCart.couponCode || "");
  };

  return (
    <div style={{ margin: "20px 0px 0 0" }}>
      <Card>
        <Title title="Playbrush Store" />
        <CardContent>
          <Typography
            variant="h1"
            sx={{
              fontSize: "20px",
              fontFamily: "Inter 24pt",
              fontWeight: 600,
              m: 0,
              py: "4px",
              px: 0,
              color: theme.palette.primary.main,
            }}
          >
            Basket
          </Typography>
          <Root>
            <Box sx={{ width: "100%" }}>
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                  <Tab label="Products" {...a11yProps(0)} />
                  <Tab label="Bundles" {...a11yProps(1)} />
                  <Tab label="Orders" {...a11yProps(2)} />
                </Tabs>
              </Box>
              <TabPanel value={tabValue} index={0}>
                <div
                  style={{
                    padding: 0,
                    height: "calc(100vh - 300px)",
                    overflow: "auto",
                    backgroundColor: "#dbdbdb",
                    borderRadius: "10px",
                  }}
                >
                  {products.isLoading ? (
                    <div style={{ fontSize: 20, fontWeight: "bold" }}>Loading Products...</div>
                  ) : products.isError ? (
                    <div style={{ fontSize: 20, fontWeight: "bold" }}>Error: {products.error.message}</div>
                  ) : (
                    <>
                      {products.data && products.data.length > 0 ? (
                        products.data.map((product) => {
                          const prices = product.prices || {};
                          const priceKey = `${currencyCart}-${selectedCountry}`;
                          const currentPrice = prices.currentPrice && prices.currentPrice[priceKey];
                          const recurringInterval = prices.recurringInterval && prices.recurringInterval[priceKey];
                          const recurringTrialPeriodDays =
                            prices.recurringTrialPeriodDays && prices.recurringTrialPeriodDays[priceKey];
                          const priceAmountAfterTrial =
                            prices.priceAmountPayAfterTrialCurrent && prices.priceAmountPayAfterTrialCurrent[priceKey];
                          const priceAmountPayNow =
                            prices.priceAmountPayNowCurrent && prices.priceAmountPayNowCurrent[priceKey];

                          return (
                            <Card sx={{ display: "flex", margin: "20px" }} key={product.productId}>
                              {product.image && (
                                <CardMedia
                                  component="img"
                                  sx={{
                                    maxWidth: 100,
                                    maxHeight: 100,
                                    backgroundSize: "contain",
                                    objectFit: "contain",
                                  }}
                                  image={product.image}
                                />
                              )}
                              {!product.image && <div style={{ width: 100, minWidth: 100, backgroundColor: "gray" }} />}
                              <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                                <CardContent sx={{ flex: "1 0 auto" }}>
                                  <Typography component="div" variant="subtitle1">
                                    {product.name}
                                  </Typography>
                                  <Typography component="div" variant="subtitle2">
                                    {recurringInterval ? (
                                      <Chip
                                        label="SUB"
                                        color="secondary"
                                        variant="outlined"
                                        style={{ fontSize: "10px" }}
                                      />
                                    ) : (
                                      <Chip label="ONE TIME" variant="outlined" style={{ fontSize: "10px" }} />
                                    )}
                                    {recurringInterval && recurringTrialPeriodDays && (
                                      <Chip
                                        label={"TRIAL " + recurringTrialPeriodDays}
                                        color="primary"
                                        variant="outlined"
                                        style={{ fontSize: "10px" }}
                                      />
                                    )}
                                  </Typography>
                                </CardContent>
                              </Box>

                              {cart.currency && currentPrice && (
                                <div style={{ display: "flex" }}>
                                  <Box
                                    sx={{
                                      minWidth: "60px",
                                      display: "flex",
                                      alignItems: "center",
                                      flexDirection: "column",
                                      justifyContent: "center",
                                      pl: 1,
                                      pb: 1,
                                      pr: 1,
                                      pt: 1,
                                    }}
                                  >
                                    {priceAmountPayNow !== undefined && <Box>{"Now "}</Box>}
                                    {priceAmountPayNow !== undefined && (
                                      <Currency price={priceAmountPayNow} currency={cart.currency} />
                                    )}
                                    {priceAmountAfterTrial && <Box>{"Then "}</Box>}
                                    {priceAmountAfterTrial && (
                                      <Box>
                                        <Currency price={priceAmountAfterTrial} currency={cart.currency} />
                                      </Box>
                                    )}
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      pl: 1,
                                      pb: 1,
                                      pr: 1,
                                      pt: 1,
                                    }}
                                  >
                                    <IconButton
                                      style={{
                                        backgroundColor: "#eee",
                                        borderRadius: "50px",
                                        fontSize: "18px",
                                      }}
                                      onClick={() => {
                                        setIsLoading(true);

                                        let cartItemListSimple = cart!.lineItems!.map((lineItem) => {
                                          return { lineId: lineItem?.lineId, quantity: lineItem?.quantity };
                                        });
                                        let cartData: GQL.MutationCartArgs = {
                                          lineItems: [
                                            ...cartItemListSimple,
                                            {
                                              lineId: currentPrice,
                                              quantity: 1,
                                            },
                                          ],
                                          cartId: state.data.cartId,
                                          country: selectedCountry,
                                          language: languageCart,
                                          currency: currencyCart,
                                          emailType: emailTypeCart,
                                        };
                                        mutationCart.mutate(cartData, {
                                          onSuccess: (data) => {
                                            setCart(data);
                                            setIsLoading(false);
                                          },
                                        });
                                      }}
                                      aria-label="add"
                                    >
                                      <Add sx={{ height: 20, width: 20 }} />
                                    </IconButton>
                                    <Link
                                      to={"/products/" + product.productId}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      <IconButton
                                        style={{
                                          backgroundColor: "#eee",
                                          borderRadius: "50px",
                                          fontSize: "18px",
                                          marginTop: "6px",
                                        }}
                                        aria-label="edit"
                                      >
                                        <Edit sx={{ height: 20, width: 20 }} />
                                      </IconButton>
                                    </Link>
                                  </Box>
                                </div>
                              )}
                            </Card>
                          );
                        })
                      ) : (
                        <div style={{ fontSize: 20, fontWeight: "bold" }}>No Item found</div>
                      )}
                    </>
                  )}
                </div>
                <div>
                  Product Count:
                  {products.isLoading ? (
                    <span>Loading...</span>
                  ) : products.isError ? (
                    <span>N/A</span>
                  ) : (
                    <span>{products.data ? products.data.length : ""}</span>
                  )}
                </div>
              </TabPanel>
              <TabPanel value={tabValue} index={1}>
                <div
                  style={{
                    padding: 0,
                    height: "calc(100vh - 300px)",
                    overflow: "auto",
                    backgroundColor: "#dbdbdb",
                    borderRadius: "10px",
                  }}
                >
                  {bundles.isLoading ? (
                    <div style={{ fontSize: 20, fontWeight: "bold" }}>Loading Bundles...</div>
                  ) : bundles.isError ? (
                    <div style={{ fontSize: 20, fontWeight: "bold" }}>Error: {bundles.error.message}</div>
                  ) : (
                    <>
                      {bundles.data && bundles.data.length > 0 ? (
                        bundles.data.map((bundle) => {
                          const prices = bundle.prices || {};
                          const priceKey = `${currencyCart}-${selectedCountry}`;
                          const priceAmountPayNowCurrent =
                            prices.priceAmountPayNowCurrent && prices.priceAmountPayNowCurrent[priceKey];
                          const priceAmountPayAfterTrialCurrent =
                            prices.priceAmountPayAfterTrialCurrent && prices.priceAmountPayAfterTrialCurrent[priceKey];
                          const stripePriceIdsExtended =
                            prices.stripePriceIdsExtended && prices.stripePriceIdsExtended[priceKey];

                          return (
                            <Card sx={{ display: "flex", margin: "20px" }} key={bundle.bundleId}>
                              {bundle.image && (
                                <CardMedia
                                  component="img"
                                  sx={{
                                    maxWidth: 100,
                                    maxHeight: 100,
                                    backgroundSize: "contain",
                                    objectFit: "contain",
                                  }}
                                  image={bundle.image}
                                />
                              )}
                              {!bundle.image && <div style={{ width: 100, minWidth: 100, backgroundColor: "gray" }} />}
                              <Box sx={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
                                <CardContent sx={{ flex: "1 0 auto" }}>
                                  <Typography component="div" variant="subtitle1">
                                    <Link
                                      to={`/v2Bundles/${bundle.bundleId}/show`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      style={{ color: "inherit" }}
                                    >
                                      {bundle.name}
                                    </Link>
                                    {"   "}
                                  </Typography>
                                  {bundle.productsExtended &&
                                  bundle.productsExtended.length > 0 &&
                                  stripePriceIdsExtended ? (
                                    stripePriceIdsExtended.map((item: any, index: number) => {
                                      const product = bundle.productsExtended && bundle.productsExtended[index];
                                      if (!product || !item) return null;

                                      return (
                                        <Typography
                                          component="div"
                                          variant="subtitle2"
                                          key={`${bundle.bundleId}-${product.productId}`}
                                        >
                                          <div style={{ padding: "4px 0px" }}>1 x {product.name}</div>
                                          <Typography component="div" variant="body2">
                                            {product.defaultPriceAmount !== null &&
                                              product.defaultPriceAmount !== undefined && (
                                                <Currency price={product.defaultPriceAmount} currency={currencyCart} />
                                              )}
                                            {" -> "}
                                            {item.unitAmount !== null && item.unitAmount !== undefined && (
                                              <Currency price={item.unitAmount} currency={currencyCart} />
                                            )}
                                          </Typography>
                                          <Typography component="div" variant="body2">
                                            {item.recurringInterval ? (
                                              <Chip
                                                label="SUB"
                                                color="secondary"
                                                variant="outlined"
                                                style={{ fontSize: "10px" }}
                                              />
                                            ) : (
                                              <Chip label="ONE TIME" variant="outlined" style={{ fontSize: "10px" }} />
                                            )}
                                            {item.recurringInterval && item.recurringTrialPeriodDays > 0 && (
                                              <Chip
                                                label={`TRIAL ${item.recurringTrialPeriodDays}`}
                                                color="primary"
                                                variant="outlined"
                                                style={{ fontSize: "10px" }}
                                              />
                                            )}
                                          </Typography>
                                        </Typography>
                                      );
                                    })
                                  ) : (
                                    <Typography variant="body2">No product information available</Typography>
                                  )}
                                </CardContent>
                              </Box>

                              {bundle && cart.currency && (
                                <div style={{ display: "flex" }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      justifyContent: "center",
                                      minWidth: "60px",
                                      alignItems: "center",
                                      pl: 1,
                                      pb: 1,
                                      pr: 1,
                                      pt: 1,
                                    }}
                                  >
                                    {priceAmountPayNowCurrent !== null && <Box>{"Now "}</Box>}
                                    {priceAmountPayNowCurrent !== null && priceAmountPayNowCurrent !== undefined && (
                                      <Currency price={priceAmountPayNowCurrent} currency={cart.currency} />
                                    )}
                                    {priceAmountPayAfterTrialCurrent && <Box>{"Then "}</Box>}
                                    {priceAmountPayAfterTrialCurrent && (
                                      <Box>
                                        <Currency price={priceAmountPayAfterTrialCurrent} currency={cart.currency} />
                                      </Box>
                                    )}
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      pl: 1,
                                      pb: 1,
                                      pr: 1,
                                      pt: 1,
                                    }}
                                  >
                                    <IconButton
                                      style={{
                                        backgroundColor: "#eee",
                                        borderRadius: "50px",
                                        fontSize: "18px",
                                      }}
                                      onClick={() => {
                                        setIsLoading(true);
                                        let is_in_cart = false;
                                        let cartItemListSimple = cart!.lineItems!.map((lineItem) => {
                                          if (lineItem?.lineId === bundle.bundleId) {
                                            is_in_cart = true;
                                            return {
                                              lineId: lineItem?.lineId,
                                              quantity: (lineItem?.quantity || 0) + 1,
                                            };
                                          }
                                          return { lineId: lineItem?.lineId, quantity: lineItem?.quantity };
                                        });
                                        let cartData: GQL.MutationCartArgs = {
                                          lineItems: is_in_cart
                                            ? [...cartItemListSimple]
                                            : [...cartItemListSimple, { lineId: bundle.bundleId, quantity: 1 }],
                                          cartId: state.data.cartId,
                                          country: selectedCountry,
                                          language: languageCart,
                                          currency: currencyCart,
                                          emailType: emailTypeCart,
                                        };
                                        mutationCart.mutate(cartData, {
                                          onSuccess: (data) => {
                                            setCart(data);
                                            setIsLoading(false);
                                          },
                                        });
                                      }}
                                      aria-label="add"
                                    >
                                      <Add sx={{ height: 20, width: 20 }} />
                                    </IconButton>
                                    <Link to={"/bundles/" + bundle.bundleId} target="_blank" rel="noopener noreferrer">
                                      <IconButton
                                        style={{
                                          backgroundColor: "#eee",
                                          borderRadius: "50px",
                                          fontSize: "18px",
                                          marginTop: "6px",
                                        }}
                                        aria-label="edit"
                                      >
                                        <Edit sx={{ height: 20, width: 20 }} />
                                      </IconButton>
                                    </Link>
                                  </Box>
                                </div>
                              )}
                            </Card>
                          );
                        })
                      ) : (
                        <div style={{ fontSize: 20, fontWeight: "bold" }}>No Item found</div>
                      )}
                    </>
                  )}
                </div>
                <div>
                  Bundles Count:
                  {bundles.isLoading ? (
                    <span>Loading...</span>
                  ) : bundles.isError ? (
                    <span>N/A</span>
                  ) : (
                    <span>{bundles.data ? bundles.data.length : ""}</span>
                  )}
                </div>
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Product specific Promotion Codes (Selected Locale + Bundles in Cart)
                  </Typography>
                  {promotionCodesLoading ? (
                    <Typography variant="body2">Loading promotion codes...</Typography>
                  ) : promotionCodesError ? (
                    <Typography variant="body2">Error: {promotionCodesError}</Typography>
                  ) : productPromotionCodeGroupsInCart.length > 0 ? (
                    productPromotionCodeGroupsInCart.map((group) => (
                      <Box key={group.productId} sx={{ pl: 1.5, mt: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {group.productName}
                        </Typography>
                        <Box sx={{ pl: 2, mt: 0.5 }}>
                          {group.promotionCodes.map((promotionCode: BasketPromotionCode) => (
                            <Box
                              key={`${group.productId}-${getPromotionCodeKey(promotionCode)}`}
                              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
                            >
                              <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                                <Link
                                  to={`/promotionCode/${promotionCode.id}/edit`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {promotionCode.couponCode}
                                </Link>{" "}
                                -{" "}
                                {promotionCode.percentage
                                  ? `${promotionCode.percentage}%`
                                  : `${promotionCode.currency?.toUpperCase() || ""} ${promotionCode.value || 0}`}
                              </Typography>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                  setCouponInput(promotionCode.couponCode || "");
                                  setCouponBtn(promotionCode.couponCode || "");
                                }}
                              >
                                Apply
                              </Button>
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2">
                      No product-specific promotion codes for current locale/cart.
                    </Typography>
                  )}
                </Box>
              </TabPanel>
              <TabPanel value={tabValue} index={2}>
                <React.Suspense fallback={<div style={{ fontSize: 20, fontWeight: "bold" }}>Loading Orders...</div>}>
                  <LazyLoadedBasketOrdersTab
                    cartId={state.data.cartId || cart.cartId}
                    onCartUpdated={handleCartUpdatedFromOrder}
                  />
                </React.Suspense>
              </TabPanel>
            </Box>

            <CardContent style={{ padding: "0 20px", width: "100%" }}>
              <Box sx={{ display: "flex", justifyContent: "space-evenly" }}>
                <Box>
                  <InputLabel
                    sx={{
                      fontSize: "13px",
                      fontFamily: "Inter 24pt",
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                    }}
                  >
                    Email Type
                  </InputLabel>
                  <ToggleButtonGroup
                    color="primary"
                    value={emailTypeCart}
                    exclusive
                    onChange={handleEmailTypeCartChange}
                    aria-label="Platform"
                    sx={{ height: "34px" }}
                  >
                    <ToggleButton value="">None</ToggleButton>
                    <ToggleButton value="order_confirmation">confirmation</ToggleButton>
                    <ToggleButton value="order_reminder">reminder</ToggleButton>
                    <ToggleButton value="order_renew">renew</ToggleButton>
                    <ToggleButton value="order_trial_ends">trial_ends</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "space-evenly",
                  alignContent: "center",
                }}
              >
                <Box>
                  <InputLabel
                    sx={{
                      fontSize: "13px",
                      fontFamily: "Inter 24pt",
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                    }}
                  >
                    Locale
                  </InputLabel>
                  <LocaleSelectMaterial
                    label="Locale"
                    value={locale}
                    onChange={handleLocaleChange}
                  ></LocaleSelectMaterial>
                </Box>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "space-evenly",
                  alignContent: "center",
                }}
              >
                <Box>
                  <InputLabel
                    sx={{
                      fontSize: "13px",
                      fontFamily: "Inter 24pt",
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                    }}
                  >
                    Currency
                  </InputLabel>
                  {currencyCart}
                </Box>
                <Box>
                  <InputLabel
                    sx={{
                      fontSize: "13px",
                      fontFamily: "Inter 24pt",
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                    }}
                  >
                    Language
                  </InputLabel>
                  {languageCart}
                </Box>
                <Box>
                  <InputLabel
                    sx={{
                      fontSize: "13px",
                      fontFamily: "Inter 24pt",
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                    }}
                  >
                    Country
                  </InputLabel>
                  {selectedCountry}
                </Box>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-evenly" }}>
                <Box>
                  <InputLabel
                    sx={{
                      fontSize: "13px",
                      fontFamily: "Inter 24pt",
                      fontWeight: 600,
                      color: theme.palette.primary.main,
                    }}
                  >
                    Coupon
                  </InputLabel>
                  <RedditTextField
                    variant="filled"
                    label="Coupon Code"
                    name="couponCode"
                    value={couponInput}
                    onChange={onCouponInputChange}
                    type="text"
                    sx={{}}
                  ></RedditTextField>
                </Box>
                <Button
                  onClick={() => {
                    setCouponBtn();
                  }}
                  variant="outlined"
                  color="primary"
                  sx={{ fontSize: "10px", marginTop: "18px" }}
                >
                  Apply coupon
                </Button>
              </Box>
              <Box sx={{ marginBottom: "10px" }}>
                {mutationCouponCheck.isLoading
                  ? "Checking..."
                  : mutationCouponCheck.isSuccess && mutationCouponCheck.data.valid
                  ? cart.couponCode === ""
                    ? ""
                    : "Valid: percentage " +
                      mutationCouponCheck.data.percentage +
                      " | discount value " +
                      mutationCouponCheck.data.value
                  : "Not Valid"}
              </Box>
              {cart.lineItems !== undefined && cart.lineItems && cart.currency ? (
                <>
                  <Box sx={{ width: "100%" }}>
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <Tabs value={tabValue2} onChange={handleTabChange2} aria-label="basic tabs example">
                        <Tab label="DEV" {...a11yProps(0)} />
                        <Tab label="ORDER" {...a11yProps(1)} />
                        <Tab label="MARKETING" {...a11yProps(2)} />
                      </Tabs>
                    </Box>
                    <TabPanel value={tabValue2} index={0}>
                      <CartList
                        cart={cart}
                        listType={"DEV"}
                        onDeleteLine={(record: any) => {
                          setIsLoading(true);
                          let cartItemListSimple = cart!.lineItems!.map((lineItem) => {
                            return { lineId: lineItem?.lineId, quantity: lineItem?.quantity };
                          });
                          let _items = [...cartItemListSimple];
                          const index = _items.findIndex((item) => item!.lineId === record.lineId);
                          if (index > -1) {
                            _items.splice(index, 1);
                          }
                          let cartData: GQL.MutationCartArgs = {
                            lineItems: _items,
                            cartId: state.data.cartId,
                          };
                          mutationCart.mutate(cartData, {
                            onSuccess: (data) => {
                              setCart(data);
                              setIsLoading(false);
                            },
                          });
                        }}
                        onUpdateLine={(record: any, quantity: number) => {
                          setIsLoading(true);
                          let cartItemListSimple = cart!.lineItems!.map((lineItem) => {
                            if (lineItem?.lineId === record.lineId) {
                              return { lineId: lineItem?.lineId, quantity };
                            }
                            return { lineId: lineItem?.lineId, quantity: lineItem?.quantity };
                          });
                          let cartData: GQL.MutationCartArgs = {
                            lineItems: cartItemListSimple,
                            cartId: state.data.cartId,
                            country: selectedCountry,
                            language: languageCart,
                            currency: currencyCart,
                            emailType: emailTypeCart,
                          };
                          mutationCart.mutate(cartData, {
                            onSuccess: (data) => {
                              setCart(data);
                              setIsLoading(false);
                            },
                          });
                        }}
                        translation={translation}
                        couponEligibleProductIds={appliedCouponEligibleProductIds}
                      />
                    </TabPanel>
                    <TabPanel value={tabValue2} index={1}>
                      <CartList
                        cart={cart}
                        listType={"ORDER"}
                        translation={translation}
                        couponEligibleProductIds={appliedCouponEligibleProductIds}
                      />
                    </TabPanel>
                    <TabPanel value={tabValue2} index={2}>
                      <CartList
                        cart={cart}
                        listType={"MARKETING"}
                        translation={translation}
                        couponEligibleProductIds={appliedCouponEligibleProductIds}
                      />
                    </TabPanel>
                  </Box>

                  <div style={{ padding: "10px 20px 10px 10px", textAlign: "right" }}></div>

                  <div style={{ padding: "10px 20px 10px 10px", textAlign: "right" }}></div>
                  <div style={{ padding: "10px 20px 10px 10px", textAlign: "right" }}>
                    Items Count:{cart.lineItems.length}
                  </div>
                  <Typography variant="subtitle1" style={{ marginRight: 10 }}>
                    Subtotal:
                    <Currency price={cart.amountSubtotal!} currency={cart.currency} />
                  </Typography>
                  <Typography variant="subtitle1" style={{ marginRight: 10 }}>
                    Coupon Code: {cart.couponCode}
                  </Typography>
                  <Typography variant="subtitle1" style={{ marginRight: 10 }}>
                    Discount:
                    <Currency price={cart.amountDiscount!} currency={cart.currency} />
                  </Typography>
                  <Typography variant="subtitle1" style={{ marginRight: 10 }}>
                    Shipping:
                    <Currency price={cart.amountShipping!} currency={cart.currency} />
                  </Typography>
                  <Typography variant="subtitle1" style={{ marginRight: 10 }}>
                    VAT:
                    <Currency price={cart.amountTax!} currency={cart.currency} />
                  </Typography>
                  <Typography variant="subtitle1" style={{ marginRight: 10 }}>
                    Total:
                    <Currency price={cart.amountTotal!} currency={cart.currency} />
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
                    {mutationCart.isError && <Box>{mutationCart.error.message}</Box>}
                    <Button
                      onClick={() => navigate("/checkout/" + state.data.cartId)}
                      variant="contained"
                      color="primary"
                      style={{ textTransform: "none" }}
                    >
                      Next
                    </Button>
                  </Box>
                </>
              ) : (
                ""
              )}
            </CardContent>
          </Root>

          <div>{processENV.REACT_APP_ENV_NAME}</div>
        </CardContent>
      </Card>
    </div>
  );
}

const PREFIX = "RaList";

export const ListClasses = {
  main: `${PREFIX}-main`,
  content: `${PREFIX}-content`,
  actions: `${PREFIX}-actions`,
  noResults: `${PREFIX}-noResults`,
};

const Root = styled("div", {
  name: PREFIX,
})(() => ({
  display: "flex",

  [`& .${ListClasses.main}`]: {
    flex: "1 1 auto",
    display: "flex",
    flexDirection: "column",
  },

  [`& .${ListClasses.content}`]: {
    position: "relative",
    overflow: "inherit",
  },

  [`& .${ListClasses.actions}`]: {
    zIndex: 2,
    display: "flex",
    justifyContent: "flex-end",
    flexWrap: "wrap",
  },

  [`& .${ListClasses.noResults}`]: { padding: 20 },
}));
